// Design tokeny mesketch (DES-003 §1): jediný zdroj barev, písem, velikostí, rozestupů, zaoblení,
// tlouštěk linek a délek animací. Web z nich bere proměnné CSS (`cssVariables`), Satori a šablona
// PDF konstanty TS. Komponenty nepoužívají barvu ani rozměr mimo tokeny (DES-003 AK 5).

/** Základní barvy a barvy převzaté z referenčních mockupů (DES-003 §2). */
export const color = {
  paper: "#FCF5EA",
  card: "#FFFDF9",
  line: "#EADFCB",
  ink: "#2A2520",
  graphite: "#6E655B",
  taupe: "#8F857A",
  testWho: "#3F72A6",
  testWhoBg: "#E9F0F8",
  testWhoInk: "#23384A",
  testStrength: "#C68A1E",
  testStrengthBg: "#FBEFD6",
  testStrengthInk: "#6B4D13",
  testEmotion: "#DC5F39",
  testEmotionBg: "#FBE8DC",
  testEmotionInk: "#7A3420",
  testWork: "#3E8A6E",
  // [NÁVRH] dopočítané (DES-003 §2 výpočet), potvrdí Libor ve fázi 2.
  testWorkBg: "#E5E8DB",
  testWorkInk: "#345847",
  // Tlačítka v barvě testu (zadání 1.2, 15.1), kresby a výplně berou základní barvu. Malý text
  // v barvě testu je `*Ink`: korálová silná má na papíru jen 4,39 : 1.
  testWhoStrong: "#3F72A6",
  testStrengthStrong: "#8F6211",
  testEmotionStrong: "#C24E2B",
  testWorkStrong: "#357A61",
  mutedStrong: "#746A60",
  pencilLine: "#A3988A",
  placeholder: "#D3C8B8",
  barTrack: "#F3EADB",
  barZone: "#D3C5AF",
  barFinal: "#C9DAEC",
  progressTrack: "#F0E6D6",
  dividerSoft: "#F0E7D6",
  chipDashed: "#C9BDAA",
  // Grafy Skici s portrétem (CR-015 §4, zadání 27.7): zvon a květ.
  bellFill: "#EFE5D3",
  bellMid: "#E3D6BF",
  chartAxis: "#B9AE9F",
  petalLight: "#F2C9A0",
  // [NÁVRH] Tmavý pruh pod obrazem výsledku (CR-029, předlohy Libor 3. 11. 2026): tmavý tón noční
  // oblohy obrazu testu, práce dopočítaná. Text v pruhu `paper`, podpis a legenda `bandSoft`.
  testWhoBand: "#2F3444",
  testStrengthBand: "#1C3357",
  testEmotionBand: "#1F2340",
  testWorkBand: "#213A31",
  bandSoft: "#D9D6E4",
} as const;

export type ColorToken = keyof typeof color;

/** Barvy SEL Institute, jen v komponentě loga (zadání 15.1, DES-003 AK 5). */
export const sel = {
  peach: "#EFA587",
  teal: "#5EBEB5",
  cream: "#F1E3C4",
  creamOutline: "#D9C9A6",
} as const;

/** Tmavý režim [NÁVRH]: tokeny připravené, režim vypnutý (DES-003 §2). Logo na tmavém je používá. */
export const dark = {
  background: "#2A2520",
  text: "#FCF5EA",
  secondary: "#CBBFAB",
  pencil: "#B8AE9F",
  testWho: "#9DBCDD",
} as const;

/** Odstíny ikon testů v2 (DES-003 §5): nejvýš tři odstíny jedné barvy a odlesk. */
export const icon = {
  whoFacets: ["#9DBCDD", "#7AA3CF", "#5D8DC0", "#4A7DB3", "#3F72A6", "#305F91"],
  strengthInner: "#EDBE63",
  emotionTop: "#F4A78B",
  workPortal: "#B9DECD",
  workPath: "#7DBA9F",
  // Ikony testů v3 (CR-022): části portrétu v barvách koláže (`libs/portrait`, collage a vrstvy).
  figure: "#1F4E9C",
  backdrop: "#F5C9AE",
  backdropLight: "#F9DCC8",
  petal: "#E2553A",
  petalLight: "#EE7656",
  petalCenter: "#F4C542",
  starLight: "#F2C14E",
  starDeep: "#D99A2B",
  hillLight: "#9CC59A",
  hillDeep: "#3E8A6E",
  path: "#F6EFE2",
  stone: "#F4C542",
  // Hotový test (REQ-041, Libor 21. 10. 2026): tah štětcem přes dva vystřižené zelené tvary.
  done: "#238A45",
  doneLight: "#CDEBCF",
  doneMid: "#A9D9AF",
} as const;

/**
 * Barevné sady testů: hlavní barva (kresby, šrafa), světlé pozadí, tmavý text na něm, silná barva
 * (tlačítka, malý text, bod odhadu, zadání 1.2, 15.1) a plné pásmo nejistoty po posledním kole.
 */
export const testColors = {
  who: { main: color.testWho, bg: color.testWhoBg, ink: color.testWhoInk, strong: color.testWhoStrong, zone: color.barFinal, band: color.testWhoBand },
  strength: { main: color.testStrength, bg: color.testStrengthBg, ink: color.testStrengthInk, strong: color.testStrengthStrong, zone: color.testStrengthBg, band: color.testStrengthBand },
  emotion: { main: color.testEmotion, bg: color.testEmotionBg, ink: color.testEmotionInk, strong: color.testEmotionStrong, zone: color.testEmotionBg, band: color.testEmotionBand },
  work: { main: color.testWork, bg: color.testWorkBg, ink: color.testWorkInk, strong: color.testWorkStrong, zone: color.testWorkBg, band: color.testWorkBand },
} as const;

export type TestTone = keyof typeof testColors;

/** Písma (DES-003 §3). Soubory woff2 hostuje web lokálně, žádný cizí původ za běhu. */
export const font = {
  display: "'Bricolage Grotesque Variable', 'Bricolage Grotesque', system-ui, sans-serif",
  text: "'Hanken Grotesk Variable', 'Hanken Grotesk', system-ui, sans-serif",
} as const;

/** Velikosti písma v px (DES-003 §3, mockupy). */
export const fontSize = {
  hero: 29,
  title: 25,
  typeName: 31,
  /** Souhrn výsledku jako nadpis Skici a slovo pólu výrazné karty (zadání 8.5.1, 34 až 38 px). */
  summary: 34,
  poleWord: 36,
  statement: 26,
  cardTitle: 17,
  row: 16,
  button: 15,
  text: 14,
  small: 13,
  desc: 12.5,
  meta: 12,
  label: 11,
  logo: 22,
  // Úvodní stránka (CR-026 §1): nadpis stránky, nadpis sekce, nadpis karty, perex a text karty,
  // `*Small` na mobilu.
  display: 68,
  displaySmall: 44,
  section: 38,
  sectionSmall: 28,
  cardTitleLarge: 21,
  cardTitleLargeSmall: 18,
  lead: 17,
  body: 15,
} as const;

/** Řezy písma (DES-003 §3). */
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  // Nadpisy obrazovek v mockupech (proměnné písmo má osu wght).
  bold: 700,
  heavy: 800,
} as const;

/** Rozestupy v px (DES-003 §7); 28, 64 a 120 mezi sekcemi a uvnitř sekcí úvodní stránky (CR-026). */
export const space = [4, 8, 12, 14, 16, 18, 22, 28, 64, 120] as const;

/** Zaoblení v px (DES-003 §7). */
export const radius = {
  small: 4,
  bar: 7,
  card: 14,
  panel: 16,
  screen: 22,
} as const;

/** Tloušťky linek kresby (DES-003 §6). */
export const stroke = {
  pencil: 1.3,
  pencilSecond: 0.8,
  pencilSecondOpacity: 0.5,
  ink: 1.9,
  hatch: 1.4,
  hatchPeriod: 5,
  hatchOpacity: 0.55,
} as const;

/** Rozměry prvků (DES-003 §6, §7). */
export const size = {
  barHeight: 14,
  barDot: 10,
  progressHeight: 8,
  /** Rostoucí kolečka odpovědí (zadání 8.3): nejmenší a největší průměr. */
  optionDotMin: 10,
  optionDotMax: 21,
  column: 720,
  /** Stránka výsledku na desktopu od 1024 px (CR-014 §1.2). */
  result: 1120,
  /** Obraz portrétu nejvýš (výška 5/4), aby se na tabletu vešel na obrazovku (Libor 20. 10. 2026). */
  portrait: 440,
  /** Obraz zdarma pod výsledkem nejvýš (CR-027 §5, Libor 20. 10. 2026). */
  picture: 320,
  gutter: 16,
} as const;

/** Délky animací v ms (DES-003 §4, §6, REQ-020). */
export const duration = {
  advance: 250,
  draw: 1400,
  fill: 900,
  fillDelay: 400,
  loader: 2800,
} as const;

/** Rozsah z bipolárního pruhu, mimo něj se poloha ořízne (DES-003 §6). */
export const BAR_Z_RANGE = 2.5;

/** Rozsah osy z grafu zvon (zadání 27.7, CR-015 §4). */
export const BELL_Z_RANGE = 2.6;

/** Stíny: jen stín obrazu portrétu, žádná záře (DES-003 §8, CR-015 §7); obraz v hlavě Skici bez stínu (CR-029). */
export const shadow = {
  portrait: "0 14px 34px rgba(60, 40, 20, 0.18)",
  none: "none",
} as const;

export type ContrastUse = "small" | "large" | "graphic";

export interface ContrastPair {
  readonly fg: string;
  readonly bg: string;
  readonly use: ContrastUse;
  readonly note: string;
}

/**
 * Dvojice popředí a pozadí, které UI používá (DES-003 §2). Kontrolu kontrastu dělá test
 * (DES-003 AK 1): malý text ≥ 4,5, velký text a informační grafika ≥ 3.
 */
export const CONTRAST_PAIRS: readonly ContrastPair[] = [
  { fg: color.ink, bg: color.paper, use: "small", note: "text" },
  { fg: color.ink, bg: color.card, use: "small", note: "text na kartě" },
  { fg: color.graphite, bg: color.paper, use: "small", note: "vedlejší text, štítek kreslí se" },
  { fg: color.graphite, bg: color.card, use: "small", note: "vedlejší text na kartě" },
  { fg: color.graphite, bg: color.barTrack, use: "small", note: "štítek skoro jasné" },
  { fg: color.taupe, bg: color.paper, use: "large", note: "tužkové slovo typu" },
  { fg: color.taupe, bg: color.card, use: "large", note: "tužkové slovo typu na kartě" },
  { fg: color.testWho, bg: color.paper, use: "small", note: "odkazy, malý text" },
  { fg: color.testWho, bg: color.card, use: "small", note: "odkazy na kartě" },
  { fg: color.paper, bg: color.testWho, use: "small", note: "primární tlačítko" },
  { fg: color.paper, bg: color.ink, use: "small", note: "tmavé tlačítko" },
  { fg: color.testWhoInk, bg: color.testWhoBg, use: "small", note: "štítek jasné, teaser" },
  { fg: color.testStrengthInk, bg: color.testStrengthBg, use: "small", note: "text testu silné stránky" },
  { fg: color.testEmotionInk, bg: color.testEmotionBg, use: "small", note: "text testu emoce" },
  { fg: color.testWorkInk, bg: color.testWorkBg, use: "small", note: "text testu práce" },
  { fg: color.testWho, bg: color.barTrack, use: "graphic", note: "bod odhadu jasný" },
  { fg: color.graphite, bg: color.barTrack, use: "graphic", note: "bod odhadu ostatní stavy" },
  { fg: color.testWho, bg: color.progressTrack, use: "graphic", note: "výplň ukazatele postupu" },
  { fg: color.testWho, bg: color.card, use: "graphic", note: "fokus a vybraná odpověď" },
  ...Object.values(testColors).flatMap((t) => [
    { fg: color.card, bg: t.strong, use: "small" as const, note: "tlačítko v barvě testu" },
    { fg: t.ink, bg: color.paper, use: "small" as const, note: "odkaz a malý text v barvě testu" },
    { fg: t.strong, bg: color.barTrack, use: "graphic" as const, note: "bod odhadu v barvě testu" },
    { fg: t.strong, bg: color.progressTrack, use: "graphic" as const, note: "výplň ukazatele postupu" },
    { fg: t.ink, bg: t.bg, use: "small" as const, note: "text na pozadí testu" },
  ]),
  { fg: color.mutedStrong, bg: color.paper, use: "small", note: "malý vedlejší text" },
  { fg: color.mutedStrong, bg: color.card, use: "small", note: "neaktivní pól pod škálou na kartě" },
  { fg: icon.done, bg: color.card, use: "graphic", note: "fajfka hotového testu na kartě" },
  { fg: icon.done, bg: color.paper, use: "graphic", note: "fajfka hotového testu přes okraj karty" },
  { fg: icon.done, bg: icon.doneLight, use: "graphic", note: "fajfka na vystřiženém tvaru" },
  { fg: color.ink, bg: color.bellMid, use: "graphic", note: "tečka zvonu ve středním pásmu" },
  { fg: color.ink, bg: color.bellFill, use: "graphic", note: "tečka zvonu" },
  ...Object.values(testColors).flatMap((t) => [
    { fg: color.graphite, bg: t.bg, use: "small" as const, note: "popisek na pozadí testu (vnitřní pravidla, hladina)" },
    { fg: t.strong, bg: color.card, use: "graphic" as const, note: "okraj pásma nejistoty na kartě" },
    { fg: color.paper, bg: t.band, use: "small" as const, note: "název testu a typ v pruhu pod obrazem" },
    { fg: color.bandSoft, bg: t.band, use: "small" as const, note: "podpis a legenda v pruhu pod obrazem" },
  ]),
];

/** Relativní luminance podle WCAG 2.1. */
export function luminance(hex: string): number {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex);
  if (m === null) throw new Error(`barva ${hex} není #RRGGBB`);
  const v = Number.parseInt(m[1] ?? "", 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin((v >> 16) & 255) + 0.7152 * lin((v >> 8) & 255) + 0.0722 * lin(v & 255);
}

/** Poměr kontrastu `(L1 + 0,05) / (L2 + 0,05)` podle WCAG 2.1. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

export const MIN_CONTRAST: Record<ContrastUse, number> = { small: 4.5, large: 3, graphic: 3 };

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** Proměnné CSS ze všech tokenů kromě barev SEL (ty má jen komponenta loga). */
export function cssVariables(): string {
  const vars: string[] = [];
  for (const [k, v] of Object.entries(color)) vars.push(`--c-${kebab(k)}:${v}`);
  for (const [k, v] of Object.entries(font)) vars.push(`--font-${k}:${v}`);
  for (const [k, v] of Object.entries(fontSize)) vars.push(`--fs-${kebab(k)}:${v}px`);
  for (const [k, v] of Object.entries(fontWeight)) vars.push(`--fw-${k}:${v}`);
  for (const v of space) vars.push(`--sp-${v}:${v}px`);
  for (const [k, v] of Object.entries(radius)) vars.push(`--r-${k}:${v}px`);
  for (const [k, v] of Object.entries(size)) vars.push(`--size-${kebab(k)}:${v}px`);
  for (const [k, v] of Object.entries(duration)) vars.push(`--d-${kebab(k)}:${v}ms`);
  vars.push(`--stroke-hatch:${stroke.hatch}px`, `--hatch-period:${stroke.hatchPeriod}px`);
  for (const [k, v] of Object.entries(shadow)) vars.push(`--shadow-${k}:${v}`);
  // Barva testu stránky (zadání 8.5.1): `data-tone` na obalu přepne `--c-accent*`, výchozí je modrá.
  const accent = (t: (typeof testColors)[TestTone]) => `--c-accent:${t.main};--c-accent-bg:${t.bg};--c-accent-ink:${t.ink};--c-accent-strong:${t.strong};--c-accent-band:${t.band}`;
  vars.push(accent(testColors.who));
  const tones = Object.entries(testColors).map(([k, t]) => `[data-tone="${k}"]{${accent(t)}}`);
  return `:root{${vars.join(";")}}${tones.join("")}`;
}
